/**
 * Path utilities for dynamic base path detection and management
 * Enables "build once, deploy anywhere" functionality
 */

import { getConfig } from './configLoader';

let detectedBasePath = null;
let configBasePath = null;

/**
 * Detects the base path from the current URL
 * Examples:
 * - https://example.com/video_query/  → /video_query
 * - https://example.com/video-query/  → /video-query
 * - https://example.com/app/          → /app
 * - https://example.com/              → /
 */
const detectBasePath = () => {
    if (detectedBasePath !== null) {
        return detectedBasePath;
    }

    try {
        const currentPath = window.location.pathname;
        console.log('PathUtils: Current pathname:', currentPath);

        // If we're at the root, base path is empty
        if (currentPath === '/') {
            detectedBasePath = '';
            return detectedBasePath;
        }

        // Look for common app indicators in the path
        // This assumes the app is served from a subdirectory
        const pathSegments = currentPath.split('/').filter(segment => segment.length > 0);

        if (pathSegments.length === 0) {
            detectedBasePath = '';
            return detectedBasePath;
        }

        // Common patterns for where React apps are deployed
        const commonAppPaths = [
            'video_query', 'videoquery',
            'app', 'frontend', 'client', 'ui', 'web'
        ];

        // Check if the first path segment matches common app patterns
        const firstSegment = pathSegments[0].toLowerCase();
        if (commonAppPaths.includes(firstSegment)) {
            detectedBasePath = `/${pathSegments[0]}`;
            console.log('PathUtils: Detected base path from common patterns:', detectedBasePath);
            return detectedBasePath;
        }

        // If no common pattern found, assume the first segment is the app path
        // This handles cases like /my-custom-app/
        if (pathSegments.length >= 1) {
            detectedBasePath = `/${pathSegments[0]}`;
            console.log('PathUtils: Detected base path from first segment:', detectedBasePath);
            return detectedBasePath;
        }

        // Fallback to root
        detectedBasePath = '';
        console.log('PathUtils: Defaulting to root path');
        return detectedBasePath;

    } catch (error) {
        console.error('PathUtils: Error detecting base path:', error);
        detectedBasePath = '';
        return detectedBasePath;
    }
};

/**
 * Gets the base path from configuration (if specified)
 */
const getConfigBasePath = () => {
    if (configBasePath !== null) {
        return configBasePath;
    }

    // Check for early config loaded by HTML script
    if (typeof window !== 'undefined' && window.__EARLY_CONFIG__) {
        const config = window.__EARLY_CONFIG__;
        if (config && config.basePath !== undefined) {
            configBasePath = config.basePath;
            // Ensure it starts with / and doesn't end with / (unless it's just /)
            if (configBasePath && configBasePath !== '/') {
                if (!configBasePath.startsWith('/')) {
                    configBasePath = '/' + configBasePath;
                }
                if (configBasePath.endsWith('/')) {
                    configBasePath = configBasePath.slice(0, -1);
                }
            } else if (configBasePath === '/') {
                configBasePath = '';
            }
            console.log('PathUtils: Using early loaded config base path:', configBasePath);
            return configBasePath;
        }
    }

    try {
        const config = getConfig();
        if (config && config.basePath !== undefined) {
            configBasePath = config.basePath;
            // Ensure it starts with / and doesn't end with / (unless it's just /)
            if (configBasePath && configBasePath !== '/') {
                if (!configBasePath.startsWith('/')) {
                    configBasePath = '/' + configBasePath;
                }
                if (configBasePath.endsWith('/')) {
                    configBasePath = configBasePath.slice(0, -1);
                }
            } else if (configBasePath === '/') {
                configBasePath = '';
            }
            console.log('PathUtils: Using configured base path:', configBasePath);
            return configBasePath;
        }
    } catch (error) {
        console.log('PathUtils: No configuration available yet, using detection');
    }

    configBasePath = null;
    return null;
};

/**
 * Gets the effective base path (config override > detected > root)
 * @returns {string} The base path without trailing slash (empty string for root)
 */
export const getBasePath = () => {
    // Priority: configured > detected > root
    const configured = getConfigBasePath();
    if (configured !== null) {
        return configured;
    }

    return detectBasePath();
};

/**
 * Constructs a full URL with the correct base path
 * @param {string} path - The path to append (should start with /)
 * @returns {string} Full URL with base path
 *
 * Example:
 * - getFullUrl('/') with basePath '/video_query' → 'https://example.com/video_query/'
 * - getFullUrl('/api/test') with basePath '/app' → 'https://example.com/app/api/test'
 */
export const getFullUrl = (path = '/') => {
    const basePath = getBasePath();
    const origin = window.location.origin;

    // Ensure path starts with /
    if (!path.startsWith('/')) {
        path = '/' + path;
    }

    const fullUrl = `${origin}${basePath}${path}`;
    console.log(`PathUtils: Constructed URL: ${fullUrl} (basePath: ${basePath}, path: ${path})`);
    return fullUrl;
};

/**
 * Gets just the path part with base path (no domain)
 * @param {string} path - The path to append (should start with /)
 * @returns {string} Full path with base path
 *
 * Example:
 * - getFullPath('/') with basePath '/video_query' → '/video_query/'
 * - getFullPath('/callback') with basePath '/app' → '/app/callback'
 */
export const getFullPath = (path = '/') => {
    const basePath = getBasePath();

    // Ensure path starts with /
    if (!path.startsWith('/')) {
        path = '/' + path;
    }

    return `${basePath}${path}`;
};

/**
 * Constructs a redirect URI for authentication
 * Uses the current domain and detected base path
 * @param {string} path - Optional path after base (defaults to '/')
 * @returns {string} Full redirect URI
 */
export const getRedirectUri = (path = '/') => {
    // Allow override via environment variable
    if (process.env.REACT_APP_MSAL_REDIRECT_URL) {
        console.log('PathUtils: Using configured redirect URL from env:', process.env.REACT_APP_MSAL_REDIRECT_URL);
        return process.env.REACT_APP_MSAL_REDIRECT_URL;
    }
    return getFullUrl(path);
};

/**
 * Checks if the current URL is at the expected base path
 * Useful for determining if redirects worked correctly
 */
export const isAtBasePath = () => {
    const basePath = getBasePath();
    const currentPath = window.location.pathname;

    if (basePath === '') {
        return currentPath === '/';
    }

    return currentPath === basePath || currentPath === basePath + '/';
};

/**
 * Navigates to a path within the app (respects base path)
 * @param {string} path - Path to navigate to
 */
export const navigateToPath = (path = '/') => {
    const fullUrl = getFullUrl(path);
    window.location.href = fullUrl;
};

/**
 * Clears cached base path detection (useful for testing)
 */
export const clearCache = () => {
    detectedBasePath = null;
    configBasePath = null;
};

/**
 * Gets debug information about path detection
 */
export const getDebugInfo = () => {
    return {
        currentPathname: window.location.pathname,
        currentOrigin: window.location.origin,
        detectedBasePath: detectBasePath(),
        configBasePath: getConfigBasePath(),
        effectiveBasePath: getBasePath(),
        sampleRedirectUri: getRedirectUri(),
        isAtBasePath: isAtBasePath()
    };
};